/* Knevira reviews. Production component: no sample data and no automatic mounting. */
(function (global) {
  'use strict';
  const VERSION = '2.0.0';
  const LIMITS = Object.freeze({ files: 5, fileBytes: 8 * 1024 * 1024, title: 100, body: 5000, name: 80 });
  const MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
  let instance = 0;

  class ReviewError extends Error {
    constructor(message, code = 'request_failed') { super(message); this.name = 'ReviewError'; this.code = code; }
  }

  function safeUrl(value, allowBlob = false) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(String(value), global.location?.href || 'https://knevira.hsotong.com');
      return ['https:', 'http:'].includes(url.protocol) || (allowBlob && url.protocol === 'blob:') ? url.href : null;
    } catch (_) { return null; }
  }

  function approvedCounts(data) {
    const count = Number(data?.count ?? data?.approvedCount);
    const fiveStarCount = Number(data?.fiveStarCount ?? data?.distribution?.[5]);
    if (!data || !Number.isInteger(count) || count < 0 || !Number.isInteger(fiveStarCount) || fiveStarCount < 0 || fiveStarCount > count) return null;
    return { count, fiveStarCount };
  }
  function visibleFeed(reviews) {
    if (!Array.isArray(reviews)) throw new ReviewError('The review service returned an invalid review list.', 'invalid_response');
    // The server determines ownership and approval. This extra guard rejects any explicitly private non-owner record.
    return reviews.filter(review => review?.id && (review.isOwner === true || !review.status || review.status === 'approved'))
      .map(review => ({ ...review, isOwner: review.isOwner === true }))
      .filter((review, index, all) => all.findIndex(other => other.id === review.id) === index);
  }
  function prependSubmission(review, existing) {
    return visibleFeed([{ ...review, isOwner: true }, ...existing]);
  }

  function makeController(options = {}) {
    const base = String(options.apiBase || '').replace(/\/$/, '');
    const transport = options.fetch || global.fetch?.bind(global);
    let csrfToken = null;
    let sessionQueue = Promise.resolve();
    async function request(path, method = 'GET', payload, extraHeaders = {}) {
      if (!base || !safeUrl(base)) throw new ReviewError('Reviews are not connected yet. Please try again later.', 'not_configured');
      if (global.navigator?.onLine === false) throw new ReviewError('You are offline. Reconnect and try again.', 'offline');
      if (!transport) throw new ReviewError('This browser cannot connect to reviews.', 'unsupported');
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), options.timeout || 20000);
      const headers = { Accept: 'application/json', ...extraHeaders };
      const isForm = typeof FormData !== 'undefined' && payload instanceof FormData;
      if (payload && !isForm) headers['Content-Type'] = 'application/json';
      if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken;
      try {
        const response = await transport(base + path, {
          method, credentials: 'include', cache: 'no-store', headers,
          body: payload ? (isForm ? payload : JSON.stringify(payload)) : undefined,
          signal: abort.signal
        });
        if (response.status === 204 && method === 'DELETE') return { deleted: true };
        let data;
        try { data = await response.json(); } catch (_) { throw new ReviewError('The review service returned an invalid response. Please retry.', 'invalid_response'); }
        if (!response.ok) {
          const messages = {
            401: 'Your review session has expired. Refresh your session and try again.',
            403: 'This action is not available for your session. Refresh your session and try again.',
            413: 'These photos are too large. Choose smaller photos and retry.',
            429: 'Too many requests. Please wait a moment and try again.'
          };
          throw new ReviewError(messages[response.status] || data?.message || 'The review service is unavailable. Please try again.', String(response.status));
        }
        if (!data || typeof data !== 'object') throw new ReviewError('The review service returned an invalid response.', 'invalid_response');
        return data;
      } catch (error) {
        if (error instanceof ReviewError) throw error;
        throw new ReviewError(error?.name === 'AbortError'
          ? 'The request timed out. Your review has not been confirmed. Retry to check or finish submitting.'
          : 'Unable to reach the review service. Check your connection and try again.', 'network');
      } finally { clearTimeout(timer); }
    }
    function normalizeReview(review) {
      return { ...review, createdAt: review.reviewedAt || review.createdAt,
        images: (review.images || review.media || []).map(image => {
          const entry = typeof image === 'string' ? { url: image } : image;
          let url = null;
          try { url = safeUrl(new URL(entry.url, safeUrl(base + '/')).href); } catch (_) { /* Ignore unusable media URLs. */ }
          return { ...entry, url };
        }) };
    }
    function list(data) {
      return { reviews: visibleFeed(data.reviews || data.items).map(normalizeReview), nextCursor: data.nextCursor || null, summary: data.summary || data.stats || null };
    }
    async function refreshIdentity() {
      const bootstrap = await request('/session');
      csrfToken = bootstrap.csrfToken || null;
      if (!csrfToken || !bootstrap.challenge) throw new ReviewError('A secure review session could not be established. Please retry.', 'invalid_session');
      let token = null;
      if (options.identityProxy === false && options.demo !== true) throw new ReviewError('Customer reviews are temporarily unavailable. Please try again later.', 'identity_unavailable');
      const proxy = options.identityProxy === false ? null : options.identityProxy || '/apps/knevira-reviews/identity';
      if (proxy) {
        const proxyUrl = new URL(proxy, global.location?.href || 'https://knevira.hsotong.com');
        if (global.location && proxyUrl.origin !== global.location.origin) throw new ReviewError('The review identity proxy must be on this storefront.', 'invalid_configuration');
        proxyUrl.searchParams.set('challenge', bootstrap.challenge);
        const abort = new AbortController(); const timer = setTimeout(() => abort.abort(), options.timeout || 20000);
        try {
          const response = await transport(proxyUrl.href, { credentials: 'include', cache: 'no-store', signal: abort.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new ReviewError('Could not verify your current Shopify sign-in. Please try again.', 'identity_unavailable');
          const identity = await response.json();
          if (typeof identity?.token !== 'string' || !identity.token) throw new ReviewError('Shopify identity verification returned an invalid response.', 'invalid_identity');
          token = identity.token;
        } catch (error) {
          if (error instanceof ReviewError) throw error;
          throw new ReviewError('Could not verify your current Shopify sign-in. Please check your connection and retry.', 'identity_unavailable');
        } finally { clearTimeout(timer); }
      }
      // Production guests also use a signed identity token. Null is reserved for explicit local demo mode.
      const session = await request('/session/identity', 'POST', { token });
      csrfToken = session.csrfToken || csrfToken;
      options.onSession?.(session);
      return session;
    }
    function withSession(action) {
      // Serialize identity refresh and its operation so a new challenge cannot race another request.
      const job = sessionQueue.then(async () => {
        let session;
        try { session = await refreshIdentity(); } catch (error) { options.onSessionError?.(error); throw error; }
        return action(session);
      });
      sessionQueue = job.catch(() => {});
      return job;
    }
    return {
      session() { return withSession(session => session); },
      async feed({ product, cursor, limit }) {
        const query = new URLSearchParams({ product: String(product), limit: String(limit || 6) });
        if (cursor) query.set('cursor', cursor);
        return withSession(async () => list(await request('/feed?' + query)));
      },
      async upload(files) {
        const data = new FormData();
        files.forEach(file => data.append('files', file));
        const result = await withSession(() => request('/uploads', 'POST', data));
        const images = result.images || result.media;
        if (!Array.isArray(images) || images.length !== files.length || images.some(image => !image.id)) {
          throw new ReviewError('Photo upload could not be confirmed. Please retry.', 'invalid_response');
        }
        return images;
      },
      async create(review, idempotencyKey) {
        const result = await withSession(() => request('/reviews', 'POST', review, { 'Idempotency-Key': idempotencyKey }));
        if (!result.review?.id || result.review.status !== 'pending') {
          throw new ReviewError('Your submission could not be confirmed. Please retry.', 'invalid_response');
        }
        return { ...normalizeReview(result.review), isOwner: true };
      },
      async remove(id) {
        const result = await withSession(() => request('/reviews/' + encodeURIComponent(id), 'DELETE'));
        if (result.deleted !== true) throw new ReviewError('Deletion could not be confirmed. Please retry.', 'invalid_response');
        return result;
      }
    };
  }

  function mount(root, options = {}) {
    if (!root || typeof root.replaceChildren !== 'function') throw new TypeError('A reviews root element is required.');
    const doc = root.ownerDocument;
    const controller = options.controller || makeController({ ...options, onSession(session) {
      if (state.destroyed) return;
      const oldIdentity = JSON.stringify(state.session?.customer || null);
      if (oldIdentity !== JSON.stringify(session.customer || null)) {
        state.feed.reviews = state.feed.reviews.filter(review => !review.isOwner); state.feed.cursor = null;
        renderList();
      }
      state.session = session; submit.disabled = state.busy || !productId; renderIdentity(); syncName(); options.onSession?.(session);
    }, onSessionError(error) {
      if (state.destroyed) return;
      state.session = null; state.feed.reviews = state.feed.reviews.filter(review => !review.isOwner); state.feed.cursor = null;
      submit.disabled = true; renderIdentity(error); syncName();
      renderList(); options.onSessionError?.(error);
    } });
    const product = typeof options.product === 'object' ? options.product : { id: options.product, title: options.productTitle || '' };
    const productId = product?.id || product?.handle;
    const prefix = 'kv-reviews-' + (++instance);
    const state = { session: null, files: [], urls: [], busy: false, destroyed: false,
      feed: { reviews: [], cursor: null, loaded: false }, loadToken: 0,
      uploadSignature: null, uploaded: [], submissionKey: null, submissionSignature: null };
    function el(tag, className, text) {
      const node = doc.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = String(text);
      return node;
    }
    function button(text, className, action) {
      const node = el('button', 'kvr-button ' + (className || ''), text); node.type = 'button';
      if (action) node.addEventListener('click', action);
      return node;
    }
    function message(container, text, kind = 'info', retry) {
      container.replaceChildren(); container.hidden = !text;
      container.className = 'kvr-message kvr-message--' + kind;
      if (text) container.append(el('span', '', text));
      if (retry) container.append(button('Try again', 'kvr-button--text', retry));
    }
    function announce(text, kind) { message(notice, text, kind); }
    function stars(rating) {
      const value = Math.max(0, Math.min(5, Number(rating) || 0));
      const node = el('span', 'kvr-stars'); node.setAttribute('aria-label', value + ' out of 5 stars');
      const glyph = el('span', '', '★★★★★'); glyph.setAttribute('aria-hidden', 'true');
      glyph.style.setProperty('--kvr-fill', (value / 5 * 100) + '%'); node.append(glyph); return node;
    }
    const shell = el('section', 'kvr'); shell.setAttribute('aria-labelledby', prefix + '-heading');
    const top = el('div', 'kvr-top');
    const headingGroup = el('div'); headingGroup.append(el('p', 'kvr-eyebrow', 'THE KNEVIRA COMMUNITY'));
    const heading = el('h2', 'kvr-heading', 'Customer reviews'); heading.id = prefix + '-heading'; headingGroup.append(heading);
    if (product?.title) headingGroup.append(el('p', 'kvr-muted', product.title));
    const writeButton = button('Add a review', 'kvr-button--primary', () => openForm());
    writeButton.disabled = !productId;
    top.append(headingGroup, writeButton); shell.append(top);
    const notice = el('div'); notice.hidden = true; notice.setAttribute('role', 'status'); notice.setAttribute('aria-live', 'polite'); shell.append(notice);
    const summary = el('div', 'kvr-summary'); summary.append(el('p', 'kvr-muted', 'Loading review counts…')); shell.append(summary);
    const identity = el('div', 'kvr-identity'); identity.setAttribute('role', 'status');
    const panel = el('div', 'kvr-panel'); panel.id = prefix + '-panel';
    const listMessage = el('div'); listMessage.setAttribute('role', 'status');
    const list = el('div', 'kvr-list'); const more = button('Load more reviews', 'kvr-button--secondary', () => load(true));
    more.hidden = true; panel.append(listMessage, list, more);

    const formWrap = el('section', 'kvr-form-wrap'); formWrap.hidden = true; formWrap.setAttribute('aria-labelledby', prefix + '-form-heading');
    const formHeading = el('h3', 'kvr-subheading', 'Add a review'); formHeading.id = prefix + '-form-heading';
    const form = el('form', 'kvr-form');
    const formMessage = el('div'); formMessage.hidden = true; formMessage.setAttribute('role', 'alert');
    formWrap.append(formHeading, identity, formMessage, form);
    const ratingGroup = el('fieldset', 'kvr-rating-group'); ratingGroup.append(el('legend', 'kvr-label', 'Your rating'));
    const ratingOptions = el('div', 'kvr-rating-options'); const ratingInputs = [];
    for (let value = 1; value <= 5; value++) {
      const label = el('label', 'kvr-rating-option'); const input = el('input'); input.type = 'radio'; input.name = 'rating'; input.value = String(value); input.required = true;
      input.setAttribute('aria-label', value + (value === 1 ? ' star' : ' stars')); ratingInputs.push(input);
      const visual = el('span', '', '★'); visual.setAttribute('aria-hidden', 'true'); label.append(input, visual); ratingOptions.append(label);
      input.addEventListener('change', () => ratingInputs.forEach(item => item.parentElement.classList.toggle('is-filled', Number(item.value) <= value)));
    }
    ratingGroup.append(ratingOptions); form.append(ratingGroup);
    function field(labelText, tag, name, maxLength, required = false, target = form) {
      const label = el('label', 'kvr-field'); label.append(el('span', 'kvr-label', labelText));
      const input = el(tag, 'kvr-input'); input.name = name; input.maxLength = maxLength; input.required = required;
      if (tag === 'input') input.type = 'text'; label.append(input); target.append(label); return input;
    }
    const bodyInput = field('Your review', 'textarea', 'body', LIMITS.body, true); bodyInput.rows = 5;
    bodyInput.placeholder = 'What would you like others to know?';
    const nameInput = field('Name (optional)', 'input', 'displayName', LIMITS.name); nameInput.autocomplete = 'nickname';
    const nameHelp = el('p', 'kvr-help', 'Leave blank to post anonymously.'); nameHelp.id = prefix + '-name-help';
    nameInput.setAttribute('aria-describedby', nameHelp.id); form.append(nameHelp);
    const anonLabel = el('label', 'kvr-check'); const anonymous = el('input'); anonymous.type = 'checkbox'; anonymous.name = 'anonymous';
    anonLabel.append(anonymous, el('span', '', 'Post anonymously')); form.append(anonLabel);
    anonymous.addEventListener('change', syncName);
    const extras = el('details', 'kvr-optional'); extras.append(el('summary', '', 'Add photos or a title (optional)')); form.append(extras);
    const titleInput = field('Review title', 'input', 'title', LIMITS.title, false, extras);
    const photoLabel = el('label', 'kvr-field'); photoLabel.append(el('span', 'kvr-label', 'Photos (optional)'));
    const photoInput = el('input', 'kvr-file'); photoInput.type = 'file'; photoInput.accept = 'image/jpeg,image/png,image/webp'; photoInput.multiple = true;
    photoInput.setAttribute('aria-describedby', prefix + '-photo-help'); photoLabel.append(photoInput); extras.append(photoLabel);
    const photoHelp = el('p', 'kvr-help', 'Up to 5 JPG, PNG or WebP photos, 8 MB each.'); photoHelp.id = prefix + '-photo-help'; extras.append(photoHelp);
    const previews = el('div', 'kvr-previews'); extras.append(previews);
    photoInput.addEventListener('change', () => {
      const files = [...state.files, ...Array.from(photoInput.files || [])]; photoInput.value = '';
      if (files.length > LIMITS.files) return message(formMessage, 'You can add up to 5 photos. Remove one before adding another.', 'error');
      if (files.some(file => !MIME_TYPES.has(file.type) || file.size > LIMITS.fileBytes || file.size === 0)) return message(formMessage, 'Choose JPG, PNG or WebP photos between 1 byte and 8 MB each.', 'error');
      state.files = files; state.uploadSignature = null; message(formMessage, ''); renderPreviews();
    });
    const formActions = el('div', 'kvr-form-actions'); const submit = el('button', 'kvr-button kvr-button--primary', 'Submit review'); submit.type = 'submit'; submit.disabled = true;
    const cancel = button('Cancel', 'kvr-button--secondary', () => { formWrap.hidden = true; writeButton.focus(); });
    formActions.append(submit, cancel); form.append(formActions);
    form.append(el('p', 'kvr-help', 'Your review appears here for you immediately and is shared publicly after approval.'));
    form.addEventListener('submit', submitReview); shell.append(formWrap, panel);
    root.replaceChildren(shell);

    function syncName() {
      const customer = state.session?.customer;
      nameInput.disabled = state.busy || anonymous.checked;
      nameInput.required = false;
      nameInput.parentElement.hidden = anonymous.checked || Boolean(customer?.displayName);
      nameInput.readOnly = Boolean(customer?.displayName);
      if (customer?.displayName) { nameInput.value = customer.displayName; state.autoName = true; }
      else if (state.autoName) { nameInput.value = ''; state.autoName = false; }
      nameHelp.hidden = Boolean(customer?.displayName) || anonymous.checked;
      nameHelp.textContent = anonymous.checked ? 'Your public name will be “Anonymous”.'
        : customer?.displayName ? 'Your name is supplied by your signed-in Shopify customer account.'
          : 'Leave blank to post anonymously.';
    }
    function renderIdentity(error) {
      identity.replaceChildren();
      if (error) { identity.append(el('span', '', error.message), button('Try again', 'kvr-button--text', () => connect())); return; }
      const customer = state.session?.customer;
      identity.append(el('span', '', customer?.displayName ? 'Signed in as ' + customer.displayName
        : 'Share your experience.'));
      if (!customer) {
        const loginUrl = safeUrl(state.session?.loginUrl || options.loginUrl);
        if (loginUrl) { const a = el('a', 'kvr-text-link', 'Sign in with Shopify'); a.href = loginUrl; identity.append(a); }
      }
    }
    async function connect() {
      submit.disabled = true;
      try {
        const session = await controller.session(); if (state.destroyed) return;
        if (!session || typeof session !== 'object') throw new ReviewError('Could not establish your review session. Please retry.');
        state.session = session; renderIdentity(); syncName(); submit.disabled = !productId;
      } catch (error) {
        if (state.destroyed) return; state.session = null; renderIdentity(error);
      }
    }
    function publishSummary(data, status) {
      const detail = { product: String(productId || ''), status, count: data?.count ?? null, fiveStarCount: data?.fiveStarCount ?? null };
      const EventClass = doc.defaultView?.CustomEvent || global.CustomEvent;
      if (EventClass) root.dispatchEvent(new EventClass('knevira:reviews-summary', { bubbles: true, detail }));
      options.onSummary?.(detail);
    }
    function renderSummary(data) {
      summary.replaceChildren();
      const counts = approvedCounts(data);
      if (!counts) {
        summary.append(el('p', 'kvr-muted', 'Review counts are unavailable.')); publishSummary(null, 'unavailable'); return;
      }
      publishSummary(counts, 'ready');
      const five = el('span', 'kvr-count'); five.append(el('strong', '', counts.fiveStarCount.toLocaleString('en-US')), doc.createTextNode(counts.fiveStarCount === 1 ? ' five-star review' : ' five-star reviews'));
      const total = el('span', 'kvr-count'); total.append(el('strong', '', counts.count.toLocaleString('en-US')), doc.createTextNode(counts.count === 1 ? ' total review' : ' total reviews'));
      summary.append(five, total);
    }
    function renderList() {
      const current = state.feed; list.replaceChildren(); message(listMessage, '');
      current.reviews.forEach(review => list.append(reviewCard(review)));
      more.hidden = !current.cursor; more.disabled = Boolean(current.loading);
      if (current.loading) message(listMessage, 'Loading reviews…');
      else if (current.error) message(listMessage, current.error.message, 'error', () => load(current.failedMore));
      else if (current.loaded && !current.reviews.length) message(listMessage, 'No reviews yet. Share the first review.');
    }
    async function load(append = false) {
      const current = state.feed; const token = ++state.loadToken;
      if (!productId) {
        current.error = new ReviewError('Select a product to view its reviews.'); renderList(); return;
      }
      current.loading = true; current.error = null;
      publishSummary(null, 'loading'); renderList();
      try {
        const result = await controller.feed({ product: productId, cursor: append ? current.cursor : null, limit: options.pageSize || 6 });
        if (state.destroyed || token !== state.loadToken) return;
        if (!Array.isArray(result?.reviews)) throw new ReviewError('The review service returned an invalid review list.');
        current.reviews = visibleFeed(append ? [...current.reviews, ...result.reviews] : result.reviews);
        current.cursor = result.nextCursor || null; current.loaded = true;
        renderSummary(result.summary || result.stats);
      } catch (error) {
        if (state.destroyed || token !== state.loadToken) return; current.error = error; current.failedMore = append;
        renderSummary(null);
      } finally {
        if (token === state.loadToken) { current.loading = false; if (!state.destroyed) renderList(); }
      }
    }
    function reviewCard(review) {
      const card = el('article', 'kvr-card'); const head = el('div', 'kvr-card-head'); const author = el('div', 'kvr-author');
      const name = review.anonymous ? 'Anonymous' : review.displayName || 'Anonymous';
      const avatar = el('span', 'kvr-avatar', name.slice(0, 1).toUpperCase()); avatar.setAttribute('aria-hidden', 'true');
      const byline = el('div'); byline.append(el('strong', '', name));
      if (review.reviewedAt || review.createdAt) { const date = new Date(review.reviewedAt || review.createdAt); if (!Number.isNaN(date.valueOf())) { const time = el('time', 'kvr-date', date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })); time.dateTime = date.toISOString(); byline.append(time); } }
      author.append(avatar, byline); head.append(author, stars(review.rating)); card.append(head);
      if (review.title) card.append(el('h3', 'kvr-review-title', review.title));
      card.append(el('p', 'kvr-review-body', review.body || ''));
      const reviewImages = review.images || review.media;
      if (Array.isArray(reviewImages) && reviewImages.length) {
        const photos = el('div', 'kvr-photos');
        reviewImages.forEach((photo, index) => {
          const url = safeUrl(typeof photo === 'string' ? photo : photo.url); if (!url) return;
          const a = el('a', 'kvr-photo'); a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer'; a.setAttribute('aria-label', 'Open review photo ' + (index + 1) + ' in a new tab');
          const img = el('img'); img.crossOrigin = 'use-credentials'; img.src = url; img.alt = 'Review photo ' + (index + 1); img.loading = 'lazy'; img.width = 112; img.height = 112;
          img.addEventListener('error', () => { a.replaceChildren(el('span', 'kvr-help', 'Photo unavailable')); }); a.append(img); photos.append(a);
        }); card.append(photos);
      }
      if (review.isOwner === true && review.id) card.append(button('Delete', 'kvr-button--delete', () => confirmDelete(review, card)));
      return card;
    }
    function confirmDelete(review, card) {
      if (card.querySelector('.kvr-delete-confirm')) return;
      const confirmation = el('div', 'kvr-delete-confirm'); confirmation.setAttribute('role', 'group'); confirmation.setAttribute('aria-label', 'Confirm review deletion');
      confirmation.append(el('p', '', 'Delete this review and its photos? This cannot be undone.'));
      const status = el('div'); status.setAttribute('role', 'alert');
      const deleteButton = button('Yes, delete review', 'kvr-button--danger', async () => {
        deleteButton.disabled = true; keep.disabled = true; message(status, 'Deleting review…');
        try {
          await controller.remove(review.id); if (state.destroyed) return;
          announce(options.demo ? 'DEMO ONLY: The sample review was removed from this preview.' : 'Your review was deleted.');
          state.feed.reviews = state.feed.reviews.filter(row => row.id !== review.id); renderList();
          await load(); writeButton.focus();
        } catch (error) { announce(error.message, 'error'); message(status, error.message, 'error'); deleteButton.disabled = false; keep.disabled = false; }
      });
      const keep = button('Keep review', 'kvr-button--secondary', () => { confirmation.remove(); card.querySelector('.kvr-button--delete')?.focus(); });
      confirmation.append(deleteButton, keep, status); card.append(confirmation); keep.focus();
    }
    function openForm() {
      formWrap.hidden = false; formWrap.scrollIntoView?.({ behavior: global.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
      ratingInputs[0].focus();
      if (!state.session) connect();
    }
    function renderPreviews() {
      state.urls.forEach(url => URL.revokeObjectURL(url)); state.urls = []; previews.replaceChildren();
      state.files.forEach((file, index) => {
        const item = el('div', 'kvr-preview'); const url = URL.createObjectURL(file); state.urls.push(url);
        const img = el('img'); img.src = url; img.alt = 'Selected photo ' + (index + 1); img.width = 112; img.height = 112;
        const remove = button('Remove photo ' + (index + 1), 'kvr-button--text', () => { state.files.splice(index, 1); state.uploadSignature = null; renderPreviews(); });
        remove.disabled = state.busy; item.append(img, remove); previews.append(item);
      });
    }
    function busy(value) {
      state.busy = value;
      Array.from(form.elements).forEach(input => input.disabled = value);
      if (!value) syncName();
      submit.disabled = value || !state.session || !productId;
      submit.textContent = value ? 'Submitting…' : 'Submit review'; cancel.disabled = value;
      form.setAttribute('aria-busy', String(value));
    }
    async function submitReview(event) {
      event.preventDefault(); if (state.busy) return;
      if (!state.session || !productId) return message(formMessage, 'Your review session is unavailable. Refresh your session before submitting.', 'error');
      if (global.navigator?.onLine === false) return message(formMessage, 'You are offline. Reconnect before submitting. Your draft is still here.', 'error');
      const rating = Number(ratingInputs.find(input => input.checked)?.value);
      const body = bodyInput.value.trim(); const name = nameInput.value.trim();
      if (!rating || !body) return message(formMessage, 'Choose a star rating and write your review.', 'error');
      const isAnonymous = anonymous.checked || (!state.session.customer && !name);
      const draft = { product: String(productId), rating, title: titleInput.value.trim(), body,
        displayMode: isAnonymous ? 'anonymous' : state.session.customer ? 'customer' : 'named', displayName: isAnonymous ? undefined : name };
      const fileSignature = state.files.map(file => [file.name, file.size, file.lastModified].join(':')).join('|');
      const signature = JSON.stringify(draft) + fileSignature;
      if (signature !== state.submissionSignature) {
        state.submissionKey = global.crypto?.randomUUID?.() || prefix + '-' + Date.now() + '-' + Math.random().toString(36).slice(2);
        state.submissionSignature = signature;
      }
      busy(true); message(formMessage, state.files.length ? 'Uploading your photos and submitting your review…' : 'Submitting your review…');
      try {
        if (state.files.length && fileSignature !== state.uploadSignature) {
          state.uploaded = await controller.upload(state.files); state.uploadSignature = fileSignature;
        }
        const review = await controller.create({ ...draft, mediaIds: state.files.length ? state.uploaded.map(image => image.id) : [] }, state.submissionKey);
        if (state.destroyed) return;
        if (!review?.id || review.status !== 'pending') throw new ReviewError('Your submission could not be confirmed. Please retry.');
        // Insert only the confirmed server response into this owner's local view. Public counters are untouched.
        ++state.loadToken; state.feed.loading = false; state.feed.error = null; state.feed.loaded = true;
        state.feed.reviews = prependSubmission(review, state.feed.reviews); renderList();
        form.reset(); state.files = []; state.uploaded = []; state.uploadSignature = null; state.submissionSignature = null; renderPreviews();
        ratingInputs.forEach(input => input.parentElement.classList.remove('is-filled'));
        message(formMessage, ''); formWrap.hidden = true;
        announce(options.demo ? 'DEMO ONLY: Sample submission saved in this preview. Nothing was sent to Knevira.'
          : 'Thanks — your review has been added.', 'success');
        writeButton.focus();
      } catch (error) {
        if (!state.destroyed) message(formMessage, error.message + ' Your draft has been kept. Use Submit review to retry.', 'error');
      } finally { if (!state.destroyed) busy(false); }
    }
    function offline() { announce('You are offline. Reviews cannot be submitted or deleted until you reconnect.', 'error'); }
    function online() { announce('Connection restored. Retry any unfinished action.'); }
    global.addEventListener?.('offline', offline); global.addEventListener?.('online', online);
    if (options.controller) connect();
    load();
    return {
      async refresh() {
        state.feed.loaded = false; state.feed.reviews = []; state.feed.cursor = null; renderList();
        if (options.controller) await connect();
        return load();
      },
      openForm,
      destroy() { state.destroyed = true; state.urls.forEach(url => URL.revokeObjectURL(url)); global.removeEventListener?.('offline', offline); global.removeEventListener?.('online', online); root.replaceChildren(); }
    };
  }
  const api = Object.freeze({ version: VERSION, mount, makeController, ReviewError, safeUrl, approvedCounts, visibleFeed, prependSubmission, limits: LIMITS });
  if (typeof module === 'object' && module.exports) module.exports = api;
  else global.KneviraReviews = api;
})(typeof window !== 'undefined' ? window : globalThis);
